/**
 * Engine synthesis core (plan P2a, R§5): pure, DOM-free, shared by the AudioWorklet and the node tests.
 *
 * One synth renders every voice. A V6 four-stroke fires 3 times per crank turn, so the firing
 * frequency is rpm/20 (order 3); the other partials are crank orders (multiples of 0.5). Throttle
 * brightens the upper partials and drives the noise. A gear change dips the throttle and adds a short
 * noise burst ("shift blip"). There is no era preset: era character comes only from the telemetry.
 * Everything is seeded (mulberry32), no Math.random. Every parameter ramps linearly from its last
 * value to the new target across the next block, then holds, so an update stall holds the last values.
 */

export const MAX_VOICES = 8;
export const SYNTH_SEED = 0x1badf1;

/** (crank order, amplitude); order 3 is the firing order and always the strongest */
const PARTIALS: readonly (readonly [number, number])[] = [
  [3, 1], [4.5, 0.14], [6, 0.7], [9, 0.5], [12, 0.34], [15, 0.22], [18, 0.14], [24, 0.07],
];
/** cylinder-to-cylinder imbalance (the V6 body and rasp): low crank orders on their own, slightly detuned phase */
const LOW: readonly (readonly [number, number])[] = [[0.5, 0.1], [1, 0.2], [1.5, 0.14], [2, 0.22]];
/** fixed exhaust / intake resonances (RBJ bandpass, unity peak): [Hz, Q, gain added to the dry signal] */
const FORMANTS: readonly (readonly [number, number, number])[] = [[200, 1.2, 0.7], [1800, 1.0, 0.35]];
const BLIP_S = 0.09;
/** Every PARTIALS order is a multiple of 1.5 and every LOW order of 0.5, so each sample needs one sin/cos per
 *  phase and a recurrence for the rest instead of 12 sin calls (r141: the 8-voice synth underran phone cores). */
const H_N = Math.max(...PARTIALS.map(([o]) => Math.round(o / 1.5)));
const H_AMP = new Float64Array(H_N + 1);
for (const [o, a] of PARTIALS) H_AMP[Math.round(o / 1.5)] = a;
const H_BRIGHT = 4;   // harmonic 4 = order 6: from there on the throttle brightens
if (LOW.some(([o], k) => o !== 0.5 * (k + 1))) throw new Error('LOW orders must be 0.5, 1, 1.5, ...');

export interface VoiceParams { rpm: number; thr: number; gear: number; gain: number; pan: number }

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** equal-power pan, pan -1 (left) .. 1 (right) */
export function equalPower(pan: number): [number, number] {
  const a = (Math.min(Math.max(pan, -1), 1) + 1) * (Math.PI / 4);
  return [Math.cos(a), Math.sin(a)];
}

interface Voice {
  rpm: number; thr: number; gain: number; pan: number;      // current (ramped)
  tRpm: number; tThr: number; tGain: number; tPan: number;  // target
  gear: number;
  phase: number;      // crank cycles, wrapped to [0, 2)
  phaseLow: number;   // the low orders' own phase: seeded detune keeps 6 voices from phase-locking
  detune: number;
  lowOff: number[];
  lowC: number[];     // cos / sin of the low orders' phase offsets (2π·lowOff)
  lowS: number[];
  fs: number[];      // formant biquad states (x1, x2, y1, y2 per band)
  hp: number;         // hiss lowpass state
  blip: number;       // 1 -> 0 over BLIP_S
  lp: number;         // noise lowpass state
  rnd: () => number;
}

const newVoice = (i: number): Voice => {
  const rnd = mulberry32(SYNTH_SEED + 7919 * (i + 1));
  const phase = rnd() * 2, phaseLow = rnd() * 2, detune = (rnd() - 0.5) * 0.008, lowOff = LOW.map(() => rnd());
  return {
    rpm: 0, thr: 0, gain: 0, pan: 0, tRpm: 0, tThr: 0, tGain: 0, tPan: 0, gear: 0, phase, blip: 0, lp: 0, rnd,
    phaseLow, detune, lowOff, fs: FORMANTS.flatMap(() => [0, 0, 0, 0]), hp: 0,
    lowC: lowOff.map((o) => Math.cos(2 * Math.PI * o)), lowS: lowOff.map((o) => Math.sin(2 * Math.PI * o)),
  };
};

export class EngineSynth {
  private readonly voices: Voice[] = Array.from({ length: MAX_VOICES }, (_, i) => newVoice(i));

  private readonly bq: number[][];

  constructor(private readonly sr: number) {
    this.bq = FORMANTS.map(([f, q]) => {
      const w = 2 * Math.PI * f / sr, al = Math.sin(w) / (2 * q), a0 = 1 + al;
      return [al / a0, -2 * Math.cos(w) / a0, (1 - al) / a0];      // b0 (b1 = 0, b2 = -b0), a1, a2
    });
  }

  /** set the targets for the next block; voices past the array's end go silent */
  setTargets(voices: readonly VoiceParams[]): void {
    for (let i = 0; i < MAX_VOICES; i++) {
      const v = this.voices[i], p = voices[i];
      if (!v) continue;
      const gain = p ? Math.max(0, p.gain) : 0;
      if (p) {
        const g = Math.round(p.gear);
        // a shift blip only when both the old and the new state are audible (slot reuse is not a shift)
        if (g > 0 && v.gear > 0 && g !== v.gear && gain > 0.02 && v.tGain > 0.02) v.blip = 1;
        v.gear = g;
        v.tRpm = Math.max(0, p.rpm); v.tThr = Math.min(Math.max(p.thr, 0), 1); v.tPan = p.pan;
        if (v.tGain <= 0.02 && v.gain <= 0.02) { v.rpm = v.tRpm; v.thr = v.tThr; v.pan = v.tPan; }  // fresh slot: no glide
      }
      v.tGain = gain;
    }
  }

  process(outL: Float32Array, outR: Float32Array, n = outL.length): void {
    const sr = this.sr, TWO_PI = 2 * Math.PI;
    for (let i = 0; i < n; i++) { outL[i] = 0; outR[i] = 0; }
    const lpA = 1 - Math.exp(-TWO_PI * 2200 / sr), hissA = 1 - Math.exp(-TWO_PI * 9000 / sr);
    for (const v of this.voices) {
      const silent = v.gain < 1e-5 && v.tGain < 1e-5;
      if (silent) { v.rpm = v.tRpm; v.thr = v.tThr; v.pan = v.tPan; v.gain = 0; continue; }
      const dRpm = (v.tRpm - v.rpm) / n, dThr = (v.tThr - v.thr) / n, dGain = (v.tGain - v.gain) / n;
      // equal-power gains at the block's ends, linear between (the pan ramps across one block, then holds)
      const [l0, r0] = equalPower(v.pan), [l1, r1] = equalPower(v.tPan), dL = (l1 - l0) / n, dR = (r1 - r0) / n;
      v.pan = v.tPan;
      for (let i = 0; i < n; i++) {
        v.rpm += dRpm; v.thr += dThr; v.gain += dGain;
        v.phase += v.rpm / 60 / sr;
        if (v.phase >= 2) v.phase -= 2;
        v.phaseLow += v.rpm * (1 + v.detune) / 60 / sr;
        if (v.phaseLow >= 2) v.phaseLow -= 2;
        const blip = v.blip;
        const thr = v.thr * (1 - 0.65 * blip);
        const bright = 0.3 + 0.7 * thr;
        // sin(h·θ) for h = 1..H_N by the Chebyshev recurrence, θ = 2π·1.5·phase
        const th = 3 * Math.PI * v.phase, c2 = 2 * Math.cos(th);
        let sPrev = 0, sCur = Math.sin(th), base = 0, hi = 0;
        for (let h = 1; h <= H_N; h++) {
          const amp = H_AMP[h] ?? 0;
          if (h < H_BRIGHT) base += amp * sCur; else hi += amp * sCur;
          const sNext = c2 * sCur - sPrev; sPrev = sCur; sCur = sNext;
        }
        let s = base + hi * bright;
        // low order 0.5·(k+1): sin((k+1)α + off) = sin((k+1)α)·cos(off) + cos((k+1)α)·sin(off), α = π·phaseLow
        const al = Math.PI * v.phaseLow, ca = Math.cos(al), sa = Math.sin(al);
        let ck = ca, sk = sa;
        for (let k = 0; k < LOW.length; k++) {
          s += (sk * (v.lowC[k] ?? 1) + ck * (v.lowS[k] ?? 0)) * (LOW[k]?.[1] ?? 0);
          const cn = ck * ca - sk * sa; sk = sk * ca + ck * sa; ck = cn;
        }
        const white = v.rnd() * 2 - 1;
        v.lp += lpA * (white - v.lp);
        v.hp += hissA * (white - v.hp);
        s = s * (0.45 + 0.25 * thr) / 2.2;
        const dry = s;
        for (let k = 0; k < FORMANTS.length; k++) {      // body and rasp resonances on the harmonic part
          const c = this.bq[k] ?? [0, 0, 0], g = FORMANTS[k]?.[2] ?? 0, o = k * 4;
          const y = (c[0] ?? 0) * (dry - (v.fs[o + 1] ?? 0)) - (c[1] ?? 0) * (v.fs[o + 2] ?? 0) - (c[2] ?? 0) * (v.fs[o + 3] ?? 0);
          v.fs[o + 1] = v.fs[o] ?? 0; v.fs[o] = dry; v.fs[o + 3] = v.fs[o + 2] ?? 0; v.fs[o + 2] = y;
          s += g * y;
        }
        // noise: low band as before, plus combustion hiss out to ~9 kHz that follows the throttle (less on lift)
        s += v.lp * (0.04 + 0.2 * thr + 0.35 * blip) + (v.hp - v.lp) * (0.01 + 0.07 * thr * thr + 0.15 * blip);
        s *= v.gain;
        outL[i] = (outL[i] ?? 0) + s * (l0 + dL * (i + 1));
        outR[i] = (outR[i] ?? 0) + s * (r0 + dR * (i + 1));
        if (v.blip > 0) v.blip = Math.max(0, v.blip - 1 / (BLIP_S * sr));
      }
    }
    for (let i = 0; i < n; i++) { outL[i] = Math.tanh(outL[i] ?? 0); outR[i] = Math.tanh(outR[i] ?? 0); }
  }
}
